import { getTeacherResources } from '../../common/utils'

// Inline file contents (code solutions, answer keys) are small but each takes
// a server round trip to authorize and read. Once the listing is on screen the
// teacher's next click is almost certainly one of these files, so the page
// prefetches them in the background and the viewer opens instantly from here.
//
// Memory-only by design: contents were authorized when fetched, and keeping
// them out of localStorage means a lost license stops serving new content on
// the next SPA load, same as the browser cache would.
const contents = new Map() // cacheKey -> { contents, meta }
const inflight = new Map() // cacheKey -> Promise

// Download files are a different problem: the bytes live in a bucket with no
// CORS configuration, so the browser must navigate to a signed URL rather
// than fetch it — and the whole pack's documents run to tens of megabytes,
// far too much to pull speculatively. What IS cheap is the *ticket*: a few
// hundred bytes of JSON. Minting it on hover means the click navigates to a
// URL already in hand instead of waiting on the round trip.
const urls = new Map() // cacheKey -> { url, at }

// The server signs for five minutes. Treat a ticket as spent well before then
// so a click never lands on a URL that expired in the gap.
const URL_USABLE_MS = 3 * 60 * 1000

const keyFor = user => user.userId || user.uid || user.email || 'user'
const cacheKey = (user, path) => `${keyFor(user)}:${path}`

/** Encode a listing path for the file endpoint. */
const fileUrl = path => `file/${path.split('/').map(encodeURIComponent).join('/')}`

/** Cached contents for this inline file if already in hand, else null. */
function peekFile(user, path) {
  if (!user) {
    return null
  }
  return contents.get(cacheKey(user, path)) || null
}

/** A still-usable signed URL for this download, else null. */
function peekUrl(user, path) {
  if (!user) {
    return null
  }
  const hit = urls.get(cacheKey(user, path))
  if (!hit) {
    return null
  }
  if (Date.now() - hit.at > URL_USABLE_MS) {
    urls.delete(cacheKey(user, path))
    return null
  }
  return hit.url
}

/** Store a successful payload (used by the on-click fetch too). */
function storeFile(user, path, data) {
  if (!user || !data) {
    return
  }
  if (data.serve === 'inline') {
    contents.set(cacheKey(user, path), { contents: data.contents, meta: data.meta || null })
  } else if (data.serve === 'download' && data.url) {
    urls.set(cacheKey(user, path), { url: data.url, at: Date.now() })
  }
}

/**
 * Fetch one file into the cache — inline contents or a download ticket,
 * whichever the server returns. Joins an in-flight request for the same file;
 * failures resolve to null (a prefetch must never throw) and are not
 * remembered, so the on-click path still gets its real error handling.
 */
function fetchFile(user, path) {
  const key = cacheKey(user, path)
  if (contents.has(key)) {
    return Promise.resolve(contents.get(key))
  }
  if (inflight.has(key)) {
    return inflight.get(key)
  }
  const promise = (async () => {
    try {
      const resp = await getTeacherResources(fileUrl(path), user)
      if (!resp || typeof resp.json !== 'function' || !resp.ok) {
        return null
      }
      const data = await resp.json()
      storeFile(user, path, data)
      return data.serve === 'inline' ? contents.get(key) : null
    } catch (err) {
      return null
    } finally {
      inflight.delete(key)
    }
  })()
  inflight.set(key, promise)
  return promise
}

/**
 * Warm the ticket for one download the teacher is pointing at. Called on
 * hover and focus, so the click that follows usually navigates immediately.
 * Cheap on purpose: this pulls the signed URL, never the file's bytes.
 */
function prefetchDownloadUrl(user, file) {
  if (!user || !file || file.serve !== 'download') {
    return
  }
  if (peekUrl(user, file.path) || inflight.has(cacheKey(user, file.path))) {
    return
  }
  fetchFile(user, file.path)
}

/**
 * Warm the cache for a list of files, a couple at a time so the prefetch
 * never competes with what the teacher actually clicked. Returns a cancel
 * function: cancelling stops new requests from starting (in-flight ones are
 * left to finish and still land in the cache).
 *
 * Only inline files are swept. Documents are excluded because their bytes are
 * far too heavy to pull speculatively — a single pack's guides and extensions
 * run from 2 MB to 50 MB — and their tickets expire, so those are minted on
 * hover instead.
 */
function prefetchFiles(user, files, concurrency = 2) {
  if (!user) {
    return () => {}
  }
  const queue = files.filter(f => f && f.serve === 'inline').map(f => f.path)
  let cancelled = false
  const pump = async () => {
    while (!cancelled && queue.length > 0) {
      await fetchFile(user, queue.shift())
    }
  }
  for (let i = 0; i < concurrency; i += 1) {
    pump()
  }
  return () => {
    cancelled = true
  }
}

// Tests render the page many times inside one module instance.
function _resetFileCacheForTests() {
  contents.clear()
  inflight.clear()
  urls.clear()
}

export {
  peekFile,
  peekUrl,
  storeFile,
  fetchFile,
  prefetchDownloadUrl,
  prefetchFiles,
  _resetFileCacheForTests,
}
